// frontend/src/screens/user/MyMonthlyView.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLayout } from '../context/LayoutContext';
import {
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Snackbar,
  Chip,
  Tooltip,
  Breadcrumbs,
  Link,
  Select,
  MenuItem,
  FormControl,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  Collapse,
} from '@mui/material';
import {
  ArrowBackIos,
  ArrowForwardIos,
  Today,
  Print,
  CalendarMonth,
  NavigateNext,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  ViewAgenda,
  ViewModule,
  ExpandMore,
  ExpandLess,
  SwapHoriz,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetMonthlyShiftsByStaffQuery } from '../slices/shiftApiSlice';
import { useGetMonthlyAvailabilitiesByStaffQuery } from '../slices/availabilityApiSlice';
import { useSelector } from 'react-redux';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  assigned:              '#4caf50',
  weekendAssigned:       '#ff9800',
  agency:                '#9c27b0',
  cancelled:             '#f44336',
  sickLeave:             '#ff5252',
  sickLeavePartial:      '#ff9800',
  unavailability:        '#d32f2f',
  availabilityPending:   '#e3f2fd',
  availabilityApproved:  '#e8f5e8',
  availabilityRejected:  '#ffebee',
};

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatHoursMinutes = (totalMinutes) => {
  if (!totalMinutes || totalMinutes === 0) return '0h 0m';
  const hours   = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours   === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
};



const formatTime = (dateTime) => {
  if (!dateTime) return '';
  return new Date(dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const isOverlappingWithShifts = (availStart, availEnd, shiftsOnDate) => {
  if (!shiftsOnDate?.length) return false;
  const aStart = new Date(availStart).getTime();
  const aEnd   = new Date(availEnd).getTime();
  return shiftsOnDate.some(shift => {
    if (shift.isCancelled) return false;
    const sStart = new Date(shift.startDateTime).getTime();
    const sEnd   = new Date(shift.endDateTime).getTime();
    return aStart < sEnd && aEnd > sStart;
  });
};






// A shift can be offered for swap if it's not cancelled, not agency-filled,
// and hasn't started yet — mirrors the backend's own checks in
// getMySwapEligibleShifts / createSwapRequest.


const canRequestSwap = (shift) =>
  !shift.isCancelled && !shift.isAgencyFilled && new Date(shift.startDateTime) > new Date();

// A shift is "Approved" once a manager/admin has updated the actuals and marked it Complete
const isShiftApproved = (shift) => shift?.actualStatus === 'Complete';




// ─── Print styles ────────────────────────────────────────────────────────────

const PrintStyles = () => (
  <style>{`
    @media print {
      .no-print { display: none !important; }
      .print-page { page-break-inside: avoid !important; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      body { margin: 0 !important; padding: 20px !important; background: white !important; }
      @page { size: landscape; margin: 0.5in; }
    }
  `}</style>
);

// ─── Sub-components ──────────────────────────────────────────────────────────

/** A single colour-coded shift pill */
const ShiftPill = ({ shift, getColor, compact = false, onRequestSwap }) => (
  <Tooltip
    title={
      <Box>
        <div><strong>Time:</strong> {shift.startTime} – {shift.endTime}</div>
        <div><strong>Ward:</strong> {shift.ward}</div>
        <div><strong>Category:</strong> {shift.categoryOfStaff}</div>
        {shift.location    && <div><strong>Location:</strong> {shift.location}</div>}
        {shift.isWeekend   && <div><strong>Weekend Shift</strong></div>}
        {shift.isAgencyFilled && <div><strong>Agency Filled</strong></div>}
        {shift.isCancelled && <div><strong>Cancelled</strong></div>}
      </Box>
    }
    arrow
  >
    <Card sx={{ mb: 0.5, bgcolor: getColor(shift), color: 'white', cursor: 'default', '&:hover': { boxShadow: 2 }, position: 'relative' }}>
      <CardContent sx={{ p: compact ? '4px 6px !important' : '4px 8px !important', '&:last-child': { pb: compact ? '4px !important' : '4px !important' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontSize: compact ? '0.65rem' : '0.7rem', fontWeight: 'bold', display: 'block' }}>
              {shift.startTime}–{shift.endTime}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: compact ? '0.6rem' : '0.65rem', display: 'block' }}>
              {shift.ward}
            </Typography>
          </Box>

          {onRequestSwap && canRequestSwap(shift) && (
            <Tooltip title="Request a swap for this shift" disableInteractive>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onRequestSwap(shift.id); }}
                className="no-print"
                sx={{
                  p: 0.25,
                  ml: 0.5,
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                <SwapHoriz sx={{ fontSize: compact ? 12 : 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {shift.isOvertime && (
          <Chip
            label="OT"
            size="small"
            sx={{
              height: 14,
              fontSize: '0.55rem',
              fontWeight: 'bold',
              bgcolor: '#e65100',
              color: 'white',
              mt: 0.5,
              '& .MuiChip-label': { px: 0.5 }
            }}
          />
        )}


        {shift.isOvertime && (
  <Chip
    label="OT"
    size="small"
    sx={{
      height: 14,
      fontSize: '0.55rem',
      fontWeight: 'bold',
      bgcolor: '#e65100',
      color: 'white',
      mt: 0.5,
      '& .MuiChip-label': { px: 0.5 }
    }}
  />
)}

{isShiftApproved(shift) && (
  <Chip
    label="Approved"
    size="small"
    sx={{
      height: 14,
      fontSize: '0.55rem',
      fontWeight: 'bold',
      bgcolor: '#2e7d32',
      color: 'white',
      mt: 0.5,
      ml: 0.5,
      '& .MuiChip-label': { px: 0.5 }
    }}
  />
)}



      </CardContent>
    </Card>
  </Tooltip>
);

/** A single colour-coded availability pill */
const AvailPill = ({ avail, getBgColor, getBorderColor, compact = false }) => {
  const isUnavail = avail.isUnavailable || avail.isSickLeave;
  return (
    <Tooltip
      title={
        isUnavail ? (
          <Box>
            <div><strong>{avail.isSickLeave ? 'SICK LEAVE' : 'UNAVAILABLE'}</strong></div>
            <div><strong>Time:</strong> {avail.startTime} – {avail.endTime}</div>
            <div><strong>Type:</strong> {avail.isSickLeave ? avail.sickLeaveType : avail.unavailabilityType}</div>
            <div><strong>Reason:</strong> {avail.sicknessReason || avail.unavailabilityReason || 'Not specified'}</div>
            {avail.comments && <div><strong>Note:</strong> {avail.comments}</div>}
          </Box>
        ) : (
          <Box>
            <div><strong>Available</strong></div>
            <div><strong>Time:</strong> {avail.startTime} – {avail.endTime}</div>
            <div><strong>Status:</strong> {avail.status}</div>
            <div><strong>Duration:</strong> {formatHoursMinutes(avail.durationMinutes)}</div>
            {avail.ward    && <div><strong>Ward:</strong> {avail.ward}</div>}
            {avail.comments && <div><strong>Note:</strong> {avail.comments}</div>}
          </Box>
        )
      }
      arrow
    >
      <Box sx={{
        p: compact ? '3px 5px' : '4px 6px',
        mb: 0.5,
        borderRadius: 1,
        bgcolor: getBgColor(avail),
        color: isUnavail ? 'white' : 'inherit',
        borderLeft: isUnavail ? '3px solid #b71c1c' : `3px solid ${getBorderColor(avail)}`,
        cursor: 'default',
      }}>
        <Typography variant="caption" sx={{ fontSize: compact ? '0.58rem' : '0.6rem', fontWeight: 'bold', display: 'block' }}>
          {avail.startTime}–{avail.endTime}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: compact ? '0.55rem' : '0.55rem', display: 'block' }}>
          {avail.isSickLeave ? '🏥 SICK' : avail.isUnavailable ? '⛔ UNAVAIL' : `Avail (${avail.status})`}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ─── Agenda view (mobile) ────────────────────────────────────────────────────

const AgendaView = ({ monthInfo, myShifts, filteredAvailabilities, getShiftColor, getAvailabilityColor, getAvailabilityBorderColor, onRequestSwap }) => {
  const [expandedDays, setExpandedDays] = useState({});

  const toggleDay = (date) => setExpandedDays(prev => ({ ...prev, [date]: !prev[date] }));

  // Only show current-month days in agenda
  const currentMonthDays = monthInfo.days.filter(d => d.isCurrentMonth);

  return (
    <Stack spacing={0.5}>
      {currentMonthDays.map((day) => {
        const shifts         = myShifts[day.date]             || [];
        const availabilities = filteredAvailabilities[day.date] || [];
        const hasContent     = shifts.length > 0 || availabilities.length > 0;
        const isExpanded     = expandedDays[day.date] ?? hasContent;

        const dayLabel = day.fullDate.toLocaleDateString('en-GB', {
          weekday: 'short', day: 'numeric', month: 'short',
        });

        return (
          <Paper
            key={day.date}
            elevation={day.isToday ? 3 : 1}
            sx={{
              overflow: 'hidden',
              border: day.isToday ? '2px solid' : '1px solid',
              borderColor: day.isToday ? 'primary.main' : 'divider',
              bgcolor: !hasContent ? '#fafafa' : 'background.paper',
            }}
          >
            {/* Day header row */}
            <Box
              onClick={() => hasContent && toggleDay(day.date)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 1,
                bgcolor: day.isToday ? 'primary.main' : (day.isWeekend ? '#fff8e1' : 'transparent'),
                cursor: hasContent ? 'pointer' : 'default',
                userSelect: 'none',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: day.isToday ? 700 : (hasContent ? 600 : 400),
                    color: day.isToday ? 'white' : (hasContent ? 'text.primary' : 'text.disabled'),
                    minWidth: 120,
                  }}
                >
                  {dayLabel}
                </Typography>

                {/* Content summary chips */}
                {!isExpanded && hasContent && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {shifts.length > 0 && (
                      <Chip
                        label={`${shifts.length} shift${shifts.length > 1 ? 's' : ''}`}
                        size="small"
                        sx={{ height: 18, fontSize: '0.6rem', bgcolor: STATUS_COLORS.assigned, color: 'white' }}
                      />
                    )}
                    {availabilities.filter(a => a.isUnavailable || a.isSickLeave).length > 0 && (
                      <Chip
                        label="Unavail"
                        size="small"
                        sx={{ height: 18, fontSize: '0.6rem', bgcolor: STATUS_COLORS.unavailability, color: 'white' }}
                      />
                    )}
                    {availabilities.filter(a => !a.isUnavailable && !a.isSickLeave).length > 0 && (
                      <Chip
                        label="Avail"
                        size="small"
                        sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#1976d2', color: 'white' }}
                      />
                    )}
                  </Box>
                )}
              </Box>

              {hasContent && (
                isExpanded
                  ? <ExpandLess sx={{ fontSize: 18, color: day.isToday ? 'white' : 'text.secondary' }} />
                  : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
              )}
            </Box>

            {/* Day content */}
            <Collapse in={isExpanded && hasContent}>
              <Box sx={{ px: 1.5, pb: 1, pt: 0.5 }}>
                {shifts.map(shift => (
                  <ShiftPill key={shift.id} shift={shift} getColor={getShiftColor} compact onRequestSwap={onRequestSwap} />
                ))}
                {availabilities.map(avail => (
                  <AvailPill
                    key={avail.id}
                    avail={avail}
                    getBgColor={getAvailabilityColor}
                    getBorderColor={getAvailabilityBorderColor}
                    compact
                  />
                ))}
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );
};

// ─── Desktop grid view ───────────────────────────────────────────────────────

const GridView = ({ monthInfo, myShifts, filteredAvailabilities, getShiftColor, getAvailabilityColor, getAvailabilityBorderColor, onRequestSwap }) => (
  <Paper elevation={3} sx={{ overflow: 'hidden' }} className="print-page">
    <Box sx={{ minWidth: '900px' }}>
      {/* Day headers */}
      <Grid container sx={{ bgcolor: 'primary.main', color: 'white' }}>
        {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => (
          <Grid item xs={12 / 7} key={day} sx={{ textAlign: 'center', p: 2 }}>
            <Typography fontWeight="bold">{day}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar grid */}
      <Grid container>
        {monthInfo.days.map((day, index) => {
          const shifts         = myShifts[day.date]             || [];
          const availabilities = filteredAvailabilities[day.date] || [];

          return (
            <Grid
              item
              xs={12 / 7}
              key={day.date}
              sx={{
                minHeight: '120px',
                borderRight:  index % 7 !== 6 ? '1px solid #ddd' : 'none',
                borderBottom: '1px solid #ddd',
                bgcolor: !day.isCurrentMonth ? '#f5f5f5' : (day.isToday ? '#e3f2fd' : 'white'),
                p: 1,
                position: 'relative',
              }}
            >
              {/* Day number */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: day.isToday ? 'bold' : 'normal',
                  color: day.isToday ? 'primary.main' : (day.isCurrentMonth ? 'text.primary' : 'text.disabled'),
                  mb: 1,
                }}
              >
                {day.dayOfMonth}
                {day.isWeekend && day.isCurrentMonth && (
                  <Chip
                    label="WE"
                    size="small"
                    sx={{ ml: 0.5, height: 14, fontSize: '0.55rem', bgcolor: 'warning.light', color: 'white' }}
                  />
                )}
              </Typography>

              {shifts.map(shift => (
                <ShiftPill key={shift.id} shift={shift} getColor={getShiftColor} onRequestSwap={onRequestSwap} />
              ))}

              {availabilities.map(avail => (
                <AvailPill
                  key={avail.id}
                  avail={avail}
                  getBgColor={getAvailabilityColor}
                  getBorderColor={getAvailabilityBorderColor}
                />
              ))}

              {shifts.length === 0 && availabilities.length === 0 && day.isCurrentMonth && (
                <Typography variant="caption" sx={{ color: '#bbb', fontStyle: 'italic', fontSize: '0.6rem' }}>
                  No activity
                </Typography>
              )}
            </Grid>
          );
        })}
      </Grid>
    </Box>
  </Paper>
);

// ─── Main component ───────────────────────────────────────────────────────────

const MyMonthlyView = () => {
  const { setFluid }   = useLayout();
  const navigate       = useNavigate();
  const { userInfo }   = useSelector((state) => state.auth);
  const theme          = useTheme();
  const isMobile       = useMediaQuery(theme.breakpoints.down('sm'));   // < 600 px
  const isTablet       = useMediaQuery(theme.breakpoints.down('md'));   // < 900 px

  // Force agenda on mobile, allow toggle on tablet+
  const [forceAgenda, setForceAgenda] = useState(false);
  const useAgenda = isMobile || forceAgenda;

  // ── State ──────────────────────────────────────────────────────────────────
  const [selectedYear,  setSelectedYear]  = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedDate,  setSelectedDate]  = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [myShifts,              setMyShifts]              = useState({});
  const [myAvailabilities,      setMyAvailabilities]      = useState({});
  const [filteredAvailabilities,setFilteredAvailabilities]= useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Sync date when year/month changes
  useEffect(() => {
    setSelectedDate(new Date(selectedYear, selectedMonth, 1));
  }, [selectedYear, selectedMonth]);

  // Layout: full-width
  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  // ── Month calculation ──────────────────────────────────────────────────────
  // ── DST-safe local date formatter ─────────────────────────────────────────
  // Using toISOString() on a local `new Date(y, m, d)` converts to UTC first,
  // which shifts the date back by 1 day when the local timezone is UTC+1 or more
  // (e.g. Ireland after BST starts on the last Sunday of March).
  // Building the string directly from local integers avoids that entirely.
  const localDateString = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getMonthDays = useCallback((date) => {
    const year  = date.getFullYear();
    const month = date.getMonth();
    const firstDay   = new Date(year, month, 1);
    const lastDay    = new Date(year, month + 1, 0);
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 7 : firstDayOfWeek;
    const daysFromPrev = firstDayOfWeek - 1;
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const days = [];
    // Compare today using local midnight, not UTC
    const todayStr = localDateString(new Date());

    for (let i = daysFromPrev - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({ date: localDateString(d), dayOfMonth: prevMonthLastDay - i, isCurrentMonth: false, isToday: false, fullDate: d });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const d = new Date(year, month, i);
      const dateStr = localDateString(d);
      days.push({ date: dateStr, dayOfMonth: i, isCurrentMonth: true, isToday: dateStr === todayStr, fullDate: d, isWeekend: d.getDay() === 0 || d.getDay() === 6 });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      days.push({ date: localDateString(d), dayOfMonth: i, isCurrentMonth: false, isToday: false, fullDate: d });
    }

    return {
      year, month,
      monthName: date.toLocaleDateString('en-US', { month: 'long' }),
      days,
      startDate: days[0].date,
      endDate:   days[days.length - 1].date,
    };
  }, []);

  const monthInfo = useMemo(() => getMonthDays(selectedDate), [selectedDate, getMonthDays]);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const { data: shiftsData,        isLoading: loadingShifts }        = useGetMonthlyShiftsByStaffQuery({ startDate: monthInfo.startDate, endDate: monthInfo.endDate });
  const { data: availabilitiesData, isLoading: loadingAvailabilities } = useGetMonthlyAvailabilitiesByStaffQuery({ startDate: monthInfo.startDate, endDate: monthInfo.endDate });

  // ── Process shifts ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!shiftsData || !userInfo) return;
    const byDate = {};
    monthInfo.days.forEach(d => { byDate[d.date] = []; });

    shiftsData
      .filter(s => (s.filledBy?._id || s.filledBy) === userInfo._id)
      .forEach(s => {
        const sd  = new Date(s.startDateTime);
        const date = localDateString(sd);   // DST-safe: use local date parts
        const dow  = sd.getDay();
        if (byDate[date]) {
          byDate[date].push({
            id:              s._id,
            startDateTime:   s.startDateTime,
            endDateTime:     s.endDateTime,
            startTime:       formatTime(s.startDateTime),
            endTime:         formatTime(s.endDateTime),
            ward:            s.wardDepartmentArea,
            location:        s.hospitalLocationId?.name,
            categoryOfStaff: s.categoryOfStaff,
            isWeekend:       dow === 0 || dow === 6,
            isAgencyFilled:  !!s.filledByAgency,
            isCancelled:     s.lineStatus === 'Cancelled',
            lineStatus:      s.lineStatus,
            isOvertime:      s.isOvertime || false,   // ← add this line
            actualStatus:    s.actualStatus || '', 
          });
        }
      });

    if (!isEqual(myShifts, byDate)) setMyShifts(byDate);
  }, [shiftsData, userInfo, monthInfo.days]);

  // ── Process availabilities ─────────────────────────────────────────────────
  useEffect(() => {
    if (!availabilitiesData || !userInfo || !Object.keys(myShifts).length) return;

    const byDate      = {};
    const filteredOut = {};
    monthInfo.days.forEach(d => { byDate[d.date] = []; filteredOut[d.date] = []; });

    availabilitiesData
      .filter(a => (a.requestedBy?._id || a.requestedBy) === userInfo._id)
      .forEach(a => {
        // Use the `date` field stored by the backend — it is already the correct
        // local calendar date (e.g. "2026-03-30").  Re-deriving it via
        // toISOString() would shift back 1 day in UTC+1 timezones after DST.
        const dateStr = a.date;
        if (!byDate[dateStr]) return;
        const dow = new Date(a.startDateTime).getDay();
        byDate[dateStr].push({
          id:                  a._id,
          startDateTime:       a.startDateTime,
          endDateTime:         a.endDateTime,
          startTime:           formatTime(a.startDateTime),
          endTime:             formatTime(a.endDateTime),
          durationMinutes:     a.durationMinutes,
          comments:            a.comments,
          status:              a.status || 'Pending',
          isWeekend:           dow === 0 || dow === 6,
          categoryOfStaff:     a.categoryOfStaff || 'General',
          ward:                a.wardDepartmentArea || '',
          isSickLeave:         a.isSickLeave || false,
          sickLeaveType:       a.sickLeaveType || 'Full Day',
          sicknessReason:      a.sicknessReason || '',
          isUnavailable:       a.isUnavailable || false,
          unavailabilityReason:a.unavailabilityReason || '',
          unavailabilityType:  a.unavailabilityType || 'Full Day',
        });
      });

    Object.keys(byDate).forEach(date => {
      const shiftsOnDate = myShifts[date] || [];
      filteredOut[date]  = byDate[date].filter(a => {
        if (a.isUnavailable || a.isSickLeave) return true;
        return !isOverlappingWithShifts(a.startDateTime, a.endDateTime, shiftsOnDate);
      });
    });

    if (!isEqual(myAvailabilities, byDate)) setMyAvailabilities(byDate);
    if (!isEqual(filteredAvailabilities, filteredOut)) setFilteredAvailabilities(filteredOut);
  }, [availabilitiesData, userInfo, monthInfo.days, myShifts]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const navigateMonth = (dir) => {
    let m = selectedMonth + dir;
    let y = selectedYear;
    if (m < 0)  { m = 11; y -= 1; setSelectedYear(y); }
    if (m > 11) { m = 0;  y += 1; setSelectedYear(y); }
    setSelectedMonth(m);
  };

  const goToCurrentMonth = () => {
    const t = new Date();
    setSelectedYear(t.getFullYear());
    setSelectedMonth(t.getMonth());
  };

  const yearOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    return Array.from({ length: 7 }, (_, i) => cy - 1 + i);
  }, []);

  // ── Shift swap navigation ───────────────────────────────────────────────────
  const handleRequestSwap = useCallback((shiftId) => {
    navigate(`/my-shifts/request-swap?shiftId=${shiftId}`);
  }, [navigate]);

  // ── Colour helpers ─────────────────────────────────────────────────────────
  const getShiftColor = (s) => {
    if (s.isCancelled)     return STATUS_COLORS.cancelled;
    if (s.isAgencyFilled)  return STATUS_COLORS.agency;
    if (s.isWeekend)       return STATUS_COLORS.weekendAssigned;
    return STATUS_COLORS.assigned;
  };

  const getAvailabilityColor = (a) => {
    if (a.isUnavailable || a.isSickLeave)
      return a.isSickLeave ? STATUS_COLORS.sickLeave : STATUS_COLORS.unavailability;
    switch (a.status) {
      case 'Approved': return STATUS_COLORS.availabilityApproved;
      case 'Rejected': return STATUS_COLORS.availabilityRejected;
      default:         return STATUS_COLORS.availabilityPending;
    }
  };

  const getAvailabilityBorderColor = (a) => {
    if (a.isUnavailable || a.isSickLeave)
      return a.isSickLeave ? '#d32f2f' : '#b71c1c';
    switch (a.status) {
      case 'Approved': return '#4caf50';
      case 'Rejected': return '#f44336';
      default:         return '#2196f3';
    }
  };

  // ── Print ──────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) { showSnackbar('Please allow pop-ups to print', 'warning'); return; }

    const printHTML = `<!DOCTYPE html><html lang="en"><head>
      <meta charset="UTF-8">
      <title>My Monthly Schedule – ${MONTHS[selectedMonth]} ${selectedYear}</title>
      <style>
        @page { size: landscape; margin: 0.5in; }
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: white;
               -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .hdr  { text-align:center; border-bottom:2px solid #000; padding-bottom:10px; margin-bottom:20px; }
        table { width:100%; border-collapse:collapse; table-layout:fixed; }
        th    { background:#1976d2; color:white; padding:10px; text-align:center; border:1px solid #ddd; }
        td    { border:1px solid #ddd; vertical-align:top; height:110px; padding:5px; }
        .other{ background:#f5f5f5; color:#999; }
        .today{ background:#e3f2fd !important; border:2px solid #1976d2 !important; }
        .dn   { font-weight:bold; margin-bottom:4px; font-size:11px; }
        .sc   { padding:3px; margin:2px 0; border-radius:3px; font-size:8px; color:white; font-weight:bold; }
        .sa{background:#4caf50!important} .sw{background:#ff9800!important}
        .sg{background:#9c27b0!important} .sx{background:#f44336!important}
        .ac   { padding:2px; margin:1px 0; font-size:7px; border-left:3px solid; }
        .ap{background:#e8f5e8;border-left-color:#4caf50}
        .ar{background:#ffebee;border-left-color:#f44336}
        .an{background:#e3f2fd;border-left-color:#2196f3}
        .au{background:#d32f2f!important;color:white!important;padding:2px;margin:1px 0;font-size:7px;}
        .legend{ margin-top:15px; padding:8px; border:1px solid #ddd; font-size:9px; }
        .li{ display:inline-block; margin-right:12px; }
        .lc{ display:inline-block; width:10px; height:10px; margin-right:3px; border-radius:2px; }
        .footer{ margin-top:15px; text-align:center; font-size:8px; color:#666; }
      </style>
    </head><body>
      <div class="hdr">
        <div style="font-size:22px;font-weight:bold">My Monthly Schedule</div>
        <div style="font-size:15px;color:#555">${MONTHS[selectedMonth]} ${selectedYear}</div>
        <div style="font-size:11px;color:#666">${userInfo?.name} – ${userInfo?.jobtitle || 'Staff'}</div>
        <div style="font-size:10px;color:#999">Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
      </div>
      <table>
        <thead><tr><th>Mon</th><th>Tue</th><th>Wed</th><th>Thu</th><th>Fri</th><th>Sat</th><th>Sun</th></tr></thead>
        <tbody>
          ${Array.from({ length: 6 }, (_, w) => `<tr>${Array.from({ length: 7 }, (_, d) => {
            const day = monthInfo.days[w * 7 + d];
            if (!day) return '<td></td>';
            const shifts = myShifts[day.date] || [];
            const avails = filteredAvailabilities[day.date] || [];
            let cls = day.isCurrentMonth ? '' : 'other';
            if (day.isToday) cls = 'today';
            return `<td class="${cls}">
              <div class="dn">${day.dayOfMonth}</div>
              ${shifts.map(s => {
                let sc = 'sc '; if(s.isCancelled)sc+='sx'; else if(s.isAgencyFilled)sc+='sg'; else if(s.isWeekend)sc+='sw'; else sc+='sa';
                return `<div class="${sc}"><b>${s.startTime}–${s.endTime}</b><br>${s.ward}</div>`;
              }).join('')}
              ${avails.map(a => {
                if(a.isUnavailable||a.isSickLeave) return `<div class="au"><b>${a.startTime}–${a.endTime}</b><br>${a.isSickLeave?'SICK':'UNAVAIL'}</div>`;
                let ac='ac '; if(a.status==='Approved')ac+='ap'; else if(a.status==='Rejected')ac+='ar'; else ac+='an';
                return `<div class="${ac}"><b>${a.startTime}–${a.endTime}</b><br>Avail (${a.status})</div>`;
              }).join('')}
            </td>`;
          }).join('')}</tr>`).join('')}
        </tbody>
      </table>
      <div class="legend">
        <span class="li"><span class="lc" style="background:#4caf50"></span>Assigned</span>
        <span class="li"><span class="lc" style="background:#ff9800"></span>Weekend</span>
        <span class="li"><span class="lc" style="background:#9c27b0"></span>Agency</span>
        <span class="li"><span class="lc" style="background:#f44336"></span>Cancelled</span>
        <span class="li"><span class="lc" style="background:#d32f2f"></span>Unavail</span>
        <span class="li"><span class="lc" style="background:#ff5252"></span>Sick</span>
        <span class="li"><span class="lc" style="background:#e3f2fd;border:1px solid #2196f3"></span>Pending Avail</span>
        <span class="li"><span class="lc" style="background:#e8f5e8;border:1px solid #4caf50"></span>Approved Avail</span>
      </div>
      <div class="footer">Beaumont Hospital Staff Scheduling System · Generated ${new Date().toLocaleDateString()}</div>
      <script>window.onload=()=>{setTimeout(()=>{window.print();setTimeout(()=>window.close(),100)},500);};</script>
    </body></html>`;

    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

  const showSnackbar    = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loadingShifts || loadingAvailabilities) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: isMobile ? 1.5 : 3 }}>
      <PrintStyles />

      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        sx={{ mb: 1.5 }}
      >
        <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>
          Dashboard
        </Link>
        <Typography color="text.primary" variant={isMobile ? 'body2' : 'body1'}>
          My Monthly View
        </Typography>
      </Breadcrumbs>

      {/* ── Title row ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'}>
          My Monthly Schedule
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* View toggle: only show on tablet+ since mobile always uses agenda */}
          {!isMobile && (
            <Tooltip title={forceAgenda ? 'Switch to grid view' : 'Switch to agenda view'}>
              <IconButton
                size="small"
                onClick={() => setForceAgenda(v => !v)}
                sx={{ bgcolor: 'grey.100' }}
              >
                {forceAgenda ? <ViewModule /> : <ViewAgenda />}
              </IconButton>
            </Tooltip>
          )}

          <Button
            variant="outlined"
            startIcon={<SwapHoriz />}
            onClick={() => navigate('/my-shifts/swap-requests')}
            size={isMobile ? 'small' : 'medium'}
            className="no-print"
          >
            {isMobile ? 'Swaps' : 'Swap Requests'}
          </Button>

          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
            size={isMobile ? 'small' : 'medium'}
            sx={{ bgcolor: '#673ab7', '&:hover': { bgcolor: '#5e35b1' } }}
            className="no-print"
          >
            {isMobile ? 'Print' : 'Print Schedule'}
          </Button>
        </Box>
      </Box>

      {/* ── Navigation bar ── */}
      <Paper elevation={1} sx={{ p: isMobile ? 1.5 : 2, mb: 2, bgcolor: '#f5f5f5' }} className="no-print">

        {isMobile ? (
          /* ── Mobile nav: single compact row ── */
          <Stack spacing={1}>
            {/* Month navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <IconButton onClick={() => navigateMonth(-1)} size="small">
                <ArrowBackIos fontSize="small" />
              </IconButton>

              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {MONTHS[selectedMonth]} {selectedYear}
              </Typography>

              <IconButton onClick={() => navigateMonth(1)} size="small">
                <ArrowForwardIos fontSize="small" />
              </IconButton>
            </Box>

            {/* Year selector + Today button */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" fullWidth>
                <Select
                  value={selectedYear}
                  onChange={e => setSelectedYear(parseInt(e.target.value))}
                  sx={{ bgcolor: 'white' }}
                >
                  {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>

              <Button
                startIcon={<Today />}
                onClick={goToCurrentMonth}
                variant="outlined"
                size="small"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Today
              </Button>
            </Box>
          </Stack>
        ) : (
          /* ── Tablet / desktop nav ── */
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Year */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => setSelectedYear(y => y - 1)} size="small"><KeyboardArrowLeft /></IconButton>
                <FormControl size="small" sx={{ minWidth: 90, mx: 0.5 }}>
                  <Select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))} sx={{ bgcolor: 'white' }}>
                    {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
                <IconButton onClick={() => setSelectedYear(y => y + 1)} size="small"><KeyboardArrowRight /></IconButton>
              </Box>

              <Divider orientation="vertical" flexItem />

              {/* Month */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => navigateMonth(-1)}><ArrowBackIos /></IconButton>
                <FormControl size="small" sx={{ minWidth: 130, mx: 0.5 }}>
                  <Select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))} sx={{ bgcolor: 'white' }}>
                    {MONTHS.map((m, i) => <MenuItem key={m} value={i}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
                <IconButton onClick={() => navigateMonth(1)}><ArrowForwardIos /></IconButton>
              </Box>

              <Button startIcon={<Today />} onClick={goToCurrentMonth} variant="outlined" size="small">
                Current Month
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarMonth color="action" />
              <Typography variant="h6">{MONTHS[selectedMonth]} {selectedYear}</Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {/* ── Staff info card ── */}
      <Paper elevation={1} sx={{ p: isMobile ? 1.5 : 2, mb: 2, bgcolor: '#e3f2fd' }} className="no-print">
        {isMobile ? (
          <Stack spacing={0.5}>
            <Typography variant="body2"><strong>Name:</strong> {userInfo?.name}</Typography>
            <Typography variant="body2"><strong>Role:</strong> {userInfo?.jobtitle || 'Not specified'}</Typography>
          </Stack>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body1"><strong>Name:</strong> {userInfo?.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body1"><strong>Job Title:</strong> {userInfo?.jobtitle || 'Not specified'}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body1"><strong>Email:</strong> {userInfo?.email}</Typography>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* ── Calendar / Agenda ── */}
      {useAgenda ? (
        /* ── Mobile / forced agenda view ── */
        <AgendaView
          monthInfo={monthInfo}
          myShifts={myShifts}
          filteredAvailabilities={filteredAvailabilities}
          getShiftColor={getShiftColor}
          getAvailabilityColor={getAvailabilityColor}
          getAvailabilityBorderColor={getAvailabilityBorderColor}
          onRequestSwap={handleRequestSwap}
        />
      ) : (
        /* ── Desktop / tablet grid view ── */
        <Box sx={{ overflowX: 'auto' }}>
          <GridView
            monthInfo={monthInfo}
            myShifts={myShifts}
            filteredAvailabilities={filteredAvailabilities}
            getShiftColor={getShiftColor}
            getAvailabilityColor={getAvailabilityColor}
            getAvailabilityBorderColor={getAvailabilityBorderColor}
            onRequestSwap={handleRequestSwap}
          />
        </Box>
      )}

      {/* ── Legend ── */}
      <Paper elevation={1} sx={{ p: isMobile ? 1.5 : 2, mt: 2, bgcolor: '#f5f5f5' }} className="no-print">
        <Typography variant="subtitle2" gutterBottom>Legend</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 1 : 2 }}>
          {[
            { color: STATUS_COLORS.assigned,             label: 'Assigned Shift' },
            { color: STATUS_COLORS.weekendAssigned,      label: 'Weekend Shift' },
            { color: STATUS_COLORS.agency,               label: 'Agency Filled' },
            { color: STATUS_COLORS.cancelled,            label: 'Cancelled' },
            { color: STATUS_COLORS.unavailability,       label: 'Unavailable' },
            { color: STATUS_COLORS.sickLeave,            label: 'Sick Leave' },
            { color: STATUS_COLORS.availabilityPending,  label: 'Pending Avail',  border: '1px dashed #2196f3' },
            { color: STATUS_COLORS.availabilityApproved, label: 'Approved Avail', border: '1px dashed #4caf50' },
          ].map(({ color, label, border }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: color, borderRadius: 0.5, border: border || 'none', flexShrink: 0 }} />
              <Typography variant="caption">{label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        className="no-print"
      />
    </Box>
  );
};

export default MyMonthlyView;
